import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Trash2, Send, ShoppingCart, CheckCircle2, Clock, Flame } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Panel de carritos abandonados: muestra cada carrito con items, email, hora
 * de última actividad, si ya se envió el email y si un pedido posterior lo
 * cubre. Permite reenviar el email o eliminar el carrito manualmente.
 */
export default function AdminAbandonedCarts({ user }) {
  const navigate = useNavigate();
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeHot, setIncludeHot] = useState(false);
  const [busy, setBusy] = useState(null); // session_id currently being processed

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchCarts();
  }, [user, includeHot]);

  const fetchCarts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/abandoned-carts?include_hot=${includeHot}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCarts(data.carts || []);
    } catch (e) {
      toast.error('No se pudo cargar la lista de carritos');
    } finally {
      setLoading(false);
    }
  };

  const runCheck = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/abandoned-carts/check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Comprobación lanzada — los emails se envían en segundo plano');
      setTimeout(fetchCarts, 1500);
    } catch (e) {
      toast.error('No se pudo lanzar la comprobación');
    }
  };

  const sendOne = async (sid) => {
    setBusy(sid);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/abandoned-carts/${sid}/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json();
      if (!body.success) throw new Error(body.error || 'No se pudo enviar');
      toast.success('Email de carrito abandonado enviado');
      fetchCarts();
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar el email');
    } finally {
      setBusy(null);
    }
  };

  const deleteOne = async (sid) => {
    if (!window.confirm('Eliminar este carrito del panel? Esta acción no afecta a pedidos.')) return;
    setBusy(sid);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/admin/abandoned-carts/${sid}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Carrito eliminado');
      setCarts((prev) => prev.filter((c) => c.session_id !== sid));
    } catch (e) {
      toast.error('No se pudo eliminar el carrito');
    } finally {
      setBusy(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const totalStats = {
    total: carts.length,
    covered: carts.filter((c) => c.order_covered).length,
    sent: carts.filter((c) => c.email_recently_sent).length,
    hot: carts.filter((c) => c.is_hot).length,
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-abandoned-carts-page">
      <header className="sticky top-0 z-40 bg-white border-b-2 border-black">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 text-sm text-gray-700 hover:text-black" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4" /> Panel principal
          </Link>
          <h1 className="text-lg sm:text-xl font-bold" style={{ fontFamily: 'Playfair Display' }}>
            Carritos abandonados
          </h1>
          <Button onClick={runCheck} className="bg-black text-white hover:bg-gray-800" data-testid="run-check-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Lanzar comprobación
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Resumen */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card icon={<ShoppingCart className="w-4 h-4" />} label="Carritos" value={totalStats.total} testid="stat-total" />
          <Card icon={<Flame className="w-4 h-4 text-orange-600" />} label="Aún activos (<1h)" value={totalStats.hot} testid="stat-hot" />
          <Card icon={<Send className="w-4 h-4" />} label="Email enviado (24h)" value={totalStats.sent} testid="stat-sent" />
          <Card icon={<CheckCircle2 className="w-4 h-4 text-green-600" />} label="Con pedido posterior" value={totalStats.covered} testid="stat-covered" />
        </div>

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              checked={includeHot}
              onChange={(e) => setIncludeHot(e.target.checked)}
              className="w-4 h-4"
              data-testid="include-hot-toggle"
            />
            Mostrar también carritos activos de la última hora
          </label>
          <Button variant="outline" size="sm" onClick={fetchCarts} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Recargar
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando carritos…</div>
        ) : carts.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-gray-300 p-12 text-center" data-testid="empty-state">
            <ShoppingCart className="w-8 h-8 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-600">Ningún carrito abandonado ahora mismo. Buen trabajo.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {carts.map((c) => (
              <article
                key={c.session_id}
                className={`bg-white border-2 ${c.order_covered ? 'border-green-500' : 'border-black'} p-5`}
                data-testid={`abandoned-cart-${c.session_id}`}
              >
                <div className="flex flex-wrap justify-between gap-3 items-start mb-3">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-black" data-testid={`cart-email-${c.session_id}`}>
                        {c.email || '— sin email —'}
                      </span>
                      {c.user_name && <span className="text-xs text-gray-500">· {c.user_name}</span>}
                      {c.is_hot && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase bg-orange-100 text-orange-700 px-2 py-0.5">
                          <Flame className="w-3 h-3" /> Activo
                        </span>
                      )}
                      {c.email_recently_sent && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase bg-blue-100 text-blue-700 px-2 py-0.5">
                          <Send className="w-3 h-3" /> Email enviado
                        </span>
                      )}
                      {c.order_covered && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase bg-green-100 text-green-700 px-2 py-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Pedido #{(c.order_covered.id || '').slice(0, 8)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Última actividad: {fmtDate(c.updated_at)}
                      {c.last_email_sent_at && <span className="ml-2">· Último email: {fmtDate(c.last_email_sent_at)}</span>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{c.item_count} unidad{c.item_count === 1 ? '' : 'es'}</p>
                    <p className="text-lg font-bold text-black">{Number(c.subtotal || 0).toFixed(2)}€</p>
                  </div>
                </div>

                <ul className="divide-y divide-gray-100 text-sm">
                  {c.items.map((it, i) => (
                    <li key={`${c.session_id}-${i}`} className="py-1.5 flex justify-between gap-3">
                      <span className="truncate">
                        {it.product_name || it.name || it.product_id}
                        {it.variant && Object.keys(it.variant).length > 0 && (
                          <span className="ml-1 text-xs text-gray-500">
                            ({Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(', ')})
                          </span>
                        )}
                      </span>
                      <span className="text-gray-700 shrink-0">
                        {it.quantity} × {Number(it.price || 0).toFixed(2)}€
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-3 border-t border-gray-100 flex flex-wrap gap-2 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sendOne(c.session_id)}
                    disabled={!c.email || !!c.order_covered || busy === c.session_id}
                    data-testid={`send-email-${c.session_id}`}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {c.email_recently_sent ? 'Reenviar email' : 'Enviar recordatorio'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteOne(c.session_id)}
                    disabled={busy === c.session_id}
                    data-testid={`delete-cart-${c.session_id}`}
                    className="text-red-700 hover:text-red-900"
                  >
                    <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const Card = ({ icon, label, value, testid }) => (
  <div className="bg-white border-2 border-black p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs text-gray-600 uppercase tracking-wider">
      {icon} {label}
    </div>
    <p className="mt-2 text-2xl font-bold text-black">{value}</p>
  </div>
);
